import Configuration from '@/agilis/util/Configuration'

import FormDate from '@/agilis/components/Form/FormDate/FormDate.vue'
import FormDateEditable from '@/agilis/components/Form/FormDateEditable/FormDateEditable.vue'
import FormDatetime from '@/agilis/components/Form/FormDatetime/FormDatetime.vue'
import FormInstant from '@/agilis/components/Form/FormInstant/FormInstant.vue'
import FormNumber from '@/agilis/components/Form/FormNumber/FormNumber.vue'
import FormTime from '@/agilis/components/Form/FormTime/FormTime.vue'
import FormSelect from '@/agilis/components/Form/FormSelect/FormSelect.vue'
import FormDivider from '@/agilis/components/Form/FormDivider/FormDivider.vue'
import FormDescription from '@/agilis/components/Form/FormDescription/FormDescription.vue'
import FormText from '@/agilis/components/Form/FormText/FormText.vue'
import FormTextarea from '@/agilis/components/Form/FormTextarea/FormTextarea.vue'
import FormMarkdown from '@/agilis/components/Form/FormMarkdown/FormMarkdown.vue'
import FormDataTable from '@/agilis/components/Form/FormDataTable/FormDataTable.vue'
import FormTag from '@/agilis/components/Form/FormTag/FormTag.vue'
import FormCheckbox from '@/agilis/components/Form/FormCheckbox/FormCheckbox.vue'

import FormMixin from '@/agilis/components/Form/utils/FormMixin'
import CollapsibleDividerMixin from '@/agilis/components/Form/utils/CollapsibleDividerMixin'

import jsonLogic from '@/plugins/jsonLogic'

import L from 'lodash'
import FormRecaptcha from '@/agilis/components/Form/FormRecaptcha/FormRecaptcha.vue'

export default {
  name: 'Form',

  mixins: [FormMixin, CollapsibleDividerMixin],

  components: {
    FormDate,
    FormDateEditable,
    FormDatetime,
    FormDataTable,
    FormInstant,
    FormMarkdown,
    FormNumber,
    FormTime,
    FormSelect,
    FormDivider,
    FormDescription,
    FormText,
    FormTextarea,
    FormTag,
    FormCheckbox,
    FormRecaptcha
  },

  props: {
    /**
     * Array defining the layout of the form
     * example [{ cols: ["f1", "f2"] }]
     */
    rows: {
      type: Array,
      default: () => []
    },
    /**
     * Form model use as initial-values. Each input is v-model binded on the specific field
     */
    value: {
      type: Object,
      default: () => null
    },
    /**
     * Object containing the configuration for each form field (text, select, etc), including validation rules
     */
    fields: {
      type: Object,
      default: () => ({})
    },
    /**
     * Field accessibility function: when not provided all fields are considered accessible
     */
    userCanAccessField: {
      type: Function,
      default: () => true
    },

    /**
     * Optional callback to apply extra business logic before updating the form model
     */
    beforeChange: {
      type: Function,
      default: (delta) => delta
    },

    /**
     * Map of custom components for form rendering
     */
    customComponents: {
      type: Object,
      default: () => ({})
    },
    disabledEdit: { type: Boolean, default: false },
    existingItems: { type: Array, default: () => [] }
  },
  /**
  *
  * delta is a variable used to track changes to the formModel.
  * When a form input changes, a delta object or variable is created to represent the change.
  * This delta object is then passed through the `beforeChange` callback,
  * allowing for additional business logic to be applied.
  * If the delta object is modified by the callback, the updated delta is merged into the form model.
  * delta should only contain a single form field at a time.
  * example:
  *   delta = { "collaborsInherit": true }
  *
  */
  data () {
    let formModel = this.value
    if (!formModel) {
      // No form model provided by parent via v-model: initializing the formModel looking to the form
      // configuration.
      // NB: no binding available in this case; the parent can still access the formModel via ref
      formModel = Configuration.datatable.getDTO({
        columns: this.fields
      })
    }
    return {
      formModel
    }
  },

  computed: {
    showWhenConditions () {
      return Object.entries(this.formFields).reduce((acc, [fieldId, field]) => {
        if (field.showWhen && field.showWhen.jsonLogic) {
          const show = jsonLogic.apply(field.showWhen.jsonLogic, this.value)
          return Object.assign(acc, { [fieldId]: show })
        }
        return Object.assign(acc, { [fieldId]: true })
      }, {})
    },

    formFields () {
      return Object.entries(this.fields).reduce((acc, [fieldId, field]) => {
        const computedField = this.computedFormField(fieldId, field)

        return Object.assign(acc, {
          [fieldId]: computedField
        })
      }, {})
    },

    formLayout () {
      return this.rows.map((r) => {
        const visibileCols = r.cols
          .filter((colId) => (
            this.fields?.[colId] && // field is defined
            this.userCanAccessField(colId) && // user has permissions
            this.showWhenConditions[colId] // field has an active showWhen condition
          ))

        // Filter out line when no visible cols (or only placeholder are visible)
        return visibileCols.length > 0 && visibileCols.some(c => this.formFields[c]?.type !== 'placeholder') && {
          cols: visibileCols
        }
      }).filter(c => !!c)
    },
    visibleFormLayout () {
      return this.getVisibleRows(this.formLayout, this.formFields)
    }
  },

  methods: {
    /**
     * Decorate the field label when the field is required
     */
    fieldTitle (colConfig) {
      return Configuration.datatable.getRequiredLabel(colConfig)
    },

    handleSelectChange (delta) {
      const _delta = this.beforeChange(delta)
      Object.assign(this.formModel, _delta)
    },

    handleCheckboxChange (name, value) {
      const delta = { [name]: value }
      const _delta = this.beforeChange(Object.assign({}, delta))
      if (!L.isEqual(delta, _delta)) L.merge(this.formModel, _delta)
    },

    handleElementInput (name, value) {
      const delta = { [name]: value }
      const _delta = this.beforeChange(Object.assign({}, delta))
      if (!L.isEqual(delta, _delta)) L.merge(this.formModel, _delta)
    },

    componentUpdate (newValue) {
      this.$emit('component-update', newValue)
    }
  }
}
